import React from "react";
import {
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { formatDisplayDateTime } from "../../utils/helperFunctions";
import {
  FLAG_LABEL,
  REVIEW_REASON_LABEL,
  handleHumanize,
} from "../../utils/constants/AdIntelligence";
import PaginationFooter from "../common/PaginationFooter";
import StatusChip from "../common/StatusChip";

const HEAD_SX = {
  py: 1.5,
  px: 2,
  fontSize: "0.75rem",
  fontWeight: 600,
  color: "#6b7280",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  border: "none",
  backgroundColor: "#f9fafb",
};

const TABLE_SX = {
  fontFamily: "'DM Sans', system-ui, sans-serif",
  "& .MuiTableCell-root": {
    fontFamily: "'DM Sans', system-ui, sans-serif",
    verticalAlign: "top",
  },
  "& .MuiTableBody-root .MuiTableCell-root": {
    borderBottom: "1px solid #f3f4f6",
    py: 1.75,
    px: 2,
  },
};

const COLUMNS = ["Ad", "Tag", "Pitch", "Confidence", "Flags"];

const ReviewCell = ({ row }) => (
  <div className="min-w-0">
    <StatusChip status={`tag_review_${row.review_status}`} subtle />
    <p className="mt-1 whitespace-nowrap text-xs text-gray-500">
      {row.reviewed_at ? formatDisplayDateTime(row.reviewed_at) : "—"}
    </p>
  </div>
);

const TagCell = ({ row }) => {
  if (row.status === "needs_review") {
    return (
      <div className="min-w-0">
        <StatusChip status="tag_needs_review" subtle />
        <p className="mt-1 text-xs text-gray-500">
          {REVIEW_REASON_LABEL[row.review_reason] ||
            handleHumanize({ code: row.review_reason })}
        </p>
      </div>
    );
  }
  if (row.status === "exited") {
    return (
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900">
          {row.pitched_category}
        </p>
        <p className="text-xs text-gray-500">Belongs to another category</p>
      </div>
    );
  }
  return (
    <div className="min-w-0">
      <p className="text-sm font-medium text-gray-900">
        {row.pitched_subcategory}
        <span className="text-gray-400"> › </span>
        {row.pitched_topic}
      </p>
      {row.pain_point && (
        <p className="truncate text-xs text-gray-500">{row.pain_point}</p>
      )}
    </div>
  );
};

const ReviewRow = ({ row, active, reviewed, onOpen }) => (
  <TableRow
    hover
    onClick={() => onOpen(row)}
    className={`cursor-pointer ${active ? "bg-[#f6f5fe]" : ""}`}
  >
    <TableCell sx={{ maxWidth: 320 }}>
      <p className="truncate text-sm font-medium text-gray-900">
        {row.hook_name || row.ad_name || `Delivery #${row.ad_deliver_id}`}
      </p>
      <p className="truncate text-xs text-gray-500">
        {handleHumanize({ code: row.category })} · Delivery #{row.ad_deliver_id}
      </p>
    </TableCell>
    <TableCell sx={{ maxWidth: 360 }}>
      <TagCell row={row} />
    </TableCell>
    <TableCell>
      <span className="whitespace-nowrap text-sm text-gray-700">
        {row.pitch_type || "—"}
      </span>
    </TableCell>
    <TableCell>
      {row.confidence ? (
        <StatusChip status={`confidence_${row.confidence}`} subtle />
      ) : (
        "—"
      )}
    </TableCell>
    <TableCell>
      <div className="flex flex-wrap gap-1">
        {row.flags.map((flag) => (
          <StatusChip
            key={flag}
            status={flag}
            label={FLAG_LABEL[flag] || handleHumanize({ code: flag })}
            subtle
          />
        ))}
      </div>
    </TableCell>
    <TableCell>
      {reviewed ? (
        <ReviewCell row={row} />
      ) : (
        <span className="whitespace-nowrap text-xs text-gray-500">
          {row.tagged_at ? formatDisplayDateTime(row.tagged_at) : "—"}
        </span>
      )}
    </TableCell>
  </TableRow>
);

/** One page of a review tab; a click opens the tag. */
const ReviewTable = ({
  tab,
  rows,
  isLoading,
  totalCount,
  page,
  setPage,
  pageSize,
  onPageSizeChange,
  activeId,
  onOpen,
}) => {
  const reviewed = tab === "reviewed";
  const columns = [...COLUMNS, reviewed ? "Reviewed" : "Tagged"];
  return (
    <>
      <TableContainer sx={{ flex: 1 }}>
        <Table size="small" sx={TABLE_SX}>
          <TableHead>
            <TableRow>
              {columns.map((c) => (
                <TableCell key={c} sx={HEAD_SX}>
                  {c}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  <CircularProgress size={24} sx={{ my: 4 }} />
                </TableCell>
              </TableRow>
            )}
            {!isLoading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  <p className="py-10 text-sm text-gray-500">
                    Nothing in this queue.
                  </p>
                </TableCell>
              </TableRow>
            )}
            {!isLoading &&
              rows.map((row) => (
                <ReviewRow
                  key={row.id}
                  row={row}
                  active={row.id === activeId}
                  reviewed={reviewed}
                  onOpen={onOpen}
                />
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <PaginationFooter
        shown={rows.length}
        totalCount={totalCount}
        page={page}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={onPageSizeChange}
      />
    </>
  );
};

export default ReviewTable;
